/**
 * Module dependencies.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Badge } from 'app/components/ui/badge';
import { Button } from 'app/components/ui/button';
import { ArrowRight, Repeat, Trash2 } from 'lucide-react';
import {
  useClearCryptoBotRebalances,
  useCryptoBotConfig,
  useCryptoBotRebalances
} from 'app/hooks/requests/crypto-bot/use-crypto-bot';

/**
 * Format a timestamp as a short local datetime.
 */

function when(value: string): string {
  const date = new Date(value.replace(' ', 'T'));

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

/**
 * Money in the quote asset.
 */

function money(value: number, quote: string): string {
  return `${value.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} ${quote}`;
}

/**
 * An asset amount (quantity + symbol).
 */

function amount(qty: number, asset: string): string {
  return `${qty.toLocaleString('en-US', { maximumFractionDigits: 8 })} ${asset}`;
}

/**
 * Export `CryptoBotRebalances` component.
 *
 * Audit log of portfolio reorganizations — each row shows the assets sold and
 * bought (value moved from → to) in one rebalance cycle.
 */

export function CryptoBotRebalances(): JSX.Element {
  const [translate] = useTranslation();
  const { data: config } = useCryptoBotConfig();
  const { data, isLoading } = useCryptoBotRebalances();
  const clearRebalances = useClearCryptoBotRebalances();
  const quote = config?.quoteAsset ?? 'USDC';

  if (isLoading || !data) {
    return (
      <div className={'grid place-items-center py-20'}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-center gap-3'}>
        <Title className={'pb-0'}>{translate('cryptoBot.rebalances.title')}</Title>

        <Button
          className={'ml-auto'}
          disabled={data.length === 0 || clearRebalances.isLoading}
          onClick={() => clearRebalances.mutate()}
          size={'sm'}
          variant={'outline'}
        >
          <Trash2 className={'h-4 w-4'} />
          {translate('cryptoBot.rebalances.clear')}
        </Button>
      </div>

      <p className={'text-sm text-muted-foreground'}>{translate('cryptoBot.rebalances.description')}</p>

      {data.length === 0 ? (
        <div
          className={
            'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-muted/30 px-6 py-20 text-center'
          }
        >
          <Repeat className={'h-8 w-8 text-muted-foreground'} />

          <div className={'text-base font-semibold text-foreground'}>
            {translate('cryptoBot.rebalances.emptyTitle')}
          </div>

          <p className={'max-w-md text-sm text-muted-foreground'}>
            {translate('cryptoBot.rebalances.emptyDescription')}
          </p>
        </div>
      ) : (
        <ol className={'space-y-2'}>
          {data.map(entry => (
            <li className={'rounded-lg border border-border bg-card px-4 py-3 shadow-sm'} key={entry.id}>
              <div className={'mb-2 flex flex-wrap items-center gap-2'}>
                <span className={'text-[11px] tabular-nums text-muted-foreground'}>{when(entry.at)}</span>

                <Badge variant={entry.mode === 'live' ? 'destructive' : 'outline'}>
                  {translate(`cryptoBot.mode.${entry.mode}`)}
                </Badge>

                <span className={'text-xs text-muted-foreground'}>
                  {translate('cryptoBot.rebalances.moved', { value: money(entry.movedValue, quote) })}
                </span>
              </div>

              <div className={'flex flex-wrap items-center gap-x-3 gap-y-1 text-sm'}>
                <div className={'flex flex-wrap items-center gap-1.5'}>
                  {entry.moves.sold.length === 0 ? (
                    <span className={'text-xs text-muted-foreground'}>{'—'}</span>
                  ) : (
                    entry.moves.sold.map(move => (
                      <span
                        className={'rounded-md bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive'}
                        key={`s-${move.asset}`}
                      >
                        {`− ${amount(move.qty, move.asset)} (${money(move.value, quote)})`}
                      </span>
                    ))
                  )}
                </div>

                <ArrowRight className={'h-4 w-4 shrink-0 text-muted-foreground'} />

                <div className={'flex flex-wrap items-center gap-1.5'}>
                  {entry.moves.bought.length === 0 ? (
                    <span className={'text-xs text-muted-foreground'}>{'—'}</span>
                  ) : (
                    entry.moves.bought.map(move => (
                      <span
                        className={'rounded-md bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-600'}
                        key={`b-${move.asset}`}
                      >
                        {`+ ${amount(move.qty, move.asset)} (${money(move.value, quote)})`}
                      </span>
                    ))
                  )}
                </div>
              </div>

              {entry.moves.before && entry.moves.after ? (
                <div className={'mt-2 space-y-0.5 border-t border-border pt-2 text-[11px] tabular-nums'}>
                  <div
                    className={
                      'flex items-center justify-between gap-2 text-[10px] uppercase tracking-wide text-muted-foreground'
                    }
                  >
                    <span>{translate('cryptoBot.rebalances.beforeAfter')}</span>
                    <span>{`${translate('cryptoBot.rebalances.before')} → ${translate(
                      'cryptoBot.rebalances.after'
                    )}`}</span>
                  </div>

                  <div className={'flex items-center justify-between gap-2'}>
                    <span className={'font-medium text-foreground'}>{quote}</span>

                    <span className={'text-right text-muted-foreground'}>
                      {`${money(entry.moves.before.cash, quote)} → ${money(entry.moves.after.cash, quote)}`}
                    </span>
                  </div>

                  {Object.keys(entry.moves.before.assets).map(asset => {
                    const before = entry.moves.before?.assets[asset];
                    const after = entry.moves.after?.assets[asset];

                    return (
                      <div className={'flex items-center justify-between gap-2'} key={`ba-${asset}`}>
                        <span className={'font-medium text-foreground'}>{asset}</span>

                        <span className={'text-right text-muted-foreground'}>
                          {`${amount(before?.qty ?? 0, asset)} (${money(before?.value ?? 0, quote)})`}
                          {' → '}
                          {`${amount(after?.qty ?? 0, asset)} (${money(after?.value ?? 0, quote)})`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
